import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Card, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import Table from "../../general/table-components/table";
import PriceAuditContext from "../../../context/price-audit/context";
import { IPriceAuditLog } from "../../../models/price-audit/response";
import styles from "./styles.module.scss";

const PriceAuditTable = () => {
  const { t } = useTranslation();
  const { logs, query, loading, actions } = useContext(PriceAuditContext);

  const getFieldLabel = (field: string): string => {
    const fieldLabels: Record<string, string> = {
      deliveryFee: t("delivery_fee"),
      totalPrice: t("total_price"),
      discount: t("discount"),
      serviceFee: t("service_fee"),
      cashAmount: t("cash_amount"),
      electronicAmount: t("electronic_amount"),
    };
    return fieldLabels[field] || field;
  };

  const formatValue = (value: string | number | null): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") {
      return value.toFixed(2);
    }
    return String(value);
  };

  const columns: ColumnsType<IPriceAuditLog> = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: t("order"),
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 120,
      render: (orderNumber, record) => (
        <a
          href={`/orders/details/${record.orderId}`}
          target="_blank"
          rel="noreferrer"
        >
          {orderNumber || `#${record.orderId}`}
        </a>
      ),
    },
    {
      title: t("trip_id"),
      dataIndex: "tripId",
      key: "tripId",
      width: 80,
      render: (tripId) =>
        tripId ? (
          <a href={`/trips/details/${tripId}`} target="_blank" rel="noreferrer">
            {tripId}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: t("field_changed"),
      dataIndex: "fieldChanged",
      key: "fieldChanged",
      width: 140,
      render: (field) => <Tag color="blue">{getFieldLabel(field)}</Tag>,
    },
    {
      title: t("old_value"),
      dataIndex: "oldValue",
      key: "oldValue",
      width: 100,
      render: (value) => formatValue(value),
    },
    {
      title: t("new_value"),
      dataIndex: "newValue",
      key: "newValue",
      width: 100,
      render: (value) => formatValue(value),
    },
    {
      title: t("changed_by"),
      dataIndex: "changedByName",
      key: "changedByName",
      ellipsis: true,
    },
    {
      title: t("post_payment_change"),
      dataIndex: "paymentConfirmedBeforeChange",
      key: "paymentConfirmedBeforeChange",
      width: 120,
      render: (postPayment) =>
        postPayment ? (
          <Tag color="red">{t("yes")}</Tag>
        ) : (
          <Tag color="green">{t("no")}</Tag>
        ),
    },
    {
      title: t("change_reason"),
      dataIndex: "changeReason",
      key: "changeReason",
      ellipsis: true,
      render: (reason) =>
        reason ? (
          <Tooltip title={reason}>
            <span>{reason}</span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (date) => (date ? new Date(date).toLocaleString() : "-"),
    },
  ];

  return (
    <Card className={styles.card} title={t("price_audit_log")}>
      <div className={styles.table}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={logs?.data || []}
          loading={loading.includes("logs")}
          scroll={{ x: 1400 }}
          pagination={{
            current: query?.page,
            pageSize: query?.per_page,
            total: logs?.totalCount || 0,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ${t("of")} ${total}`,
            onChange: (page, pageSize) => {
              actions.setQuery({
                ...query,
                page,
                per_page: pageSize,
              });
            },
          }}
        />
      </div>
    </Card>
  );
};

export default PriceAuditTable;
